import type { CSSProperties } from 'react';
import { FileSearch, ShieldCheck } from 'lucide-react';
import { CONFERENCIAS, type Conferencia } from '../dados/conferencias';

/**
 * A tira que NOMEIA as conferências — a prova social que não temos logos para
 * dar, dada por especificidade: quarenta defeitos reais que o sistema pega.
 *
 * ## Por que DUAS fileiras, e não uma
 * Não é estética — é o conserto de um travamento que o dono viu no desktop e
 * a medição confirmou. Numa fileira só, o trilho media **23.577px**, acima do
 * limite de textura da GPU (16.384px na maioria): o navegador não consegue
 * promover a camada ao compositor e volta a REPINTAR na main thread a cada
 * quadro. Junto disso, a 56px/s cada quadro andava 0,94px — abaixo de 1px, o
 * navegador arredonda, e um quadro anda enquanto o outro não. Os dois
 * sintomas têm a mesma cura: encolher a camada.
 *
 * Metade das conferências em cada fileira derruba o trilho para ~11.000px, e
 * as direções opostas ainda ganham o gesto que as referências usam.
 *
 * ## O ícone diz QUAL motor pega o defeito
 * Escudo para o que o cadastro barra antes de existir documento; lupa para o
 * que o auditor acha no texto do .docx. É informação, não enfeite.
 *
 * Duas cópias por fileira dão o loop sem emenda; a segunda é `aria-hidden` —
 * leitor de tela lê a lista UMA vez, como o texto que ela é.
 */

const ICONE = { cadastro: ShieldCheck, documento: FileSearch } as const;

function Lista({
  itens,
  reversa = false,
  oculta = false,
}: {
  itens: readonly Conferencia[];
  reversa?: boolean;
  oculta?: boolean;
}): JSX.Element {
  return (
    <ul
      aria-hidden={oculta || undefined}
      className={`marquee__lista${reversa ? ' marquee__lista--reversa' : ''} gap-9 pr-9`}
    >
      {itens.map((c) => {
        const Icone = ICONE[c.onde];
        return (
          <li
            key={c.chave}
            className="text-auth-texto-secundario flex items-center gap-3 text-lg whitespace-nowrap"
          >
            <Icone
              size={18}
              className={
                c.onde === 'cadastro'
                  ? 'text-auth-marca-hover/75 shrink-0'
                  : 'text-auth-acento/75 shrink-0'
              }
              aria-hidden
            />
            {c.rotulo}
          </li>
        );
      })}
    </ul>
  );
}

/**
 * Uma fileira = DUAS listas irmãs animando por conta própria (`-100%` da
 * largura de cada uma). Nunca um trilho que as contenha: é a diferença entre
 * uma camada de ~5.000px e uma de ~10.000px, e é o que separa o Firefox
 * fluido do Firefox repintando na main thread. Ver `.marquee` em
 * `estilos/motion.css`.
 */
function Fileira({
  itens,
  duracao,
  reversa = false,
}: {
  itens: readonly Conferencia[];
  /** Segundos para a lista percorrer a própria largura. */
  duracao: number;
  reversa?: boolean;
}): JSX.Element {
  return (
    <div
      className="marquee"
      style={{ ['--marquee-duracao' as string]: `${String(duracao)}s` } as CSSProperties}
    >
      <Lista itens={itens} reversa={reversa} />
      <Lista itens={itens} reversa={reversa} oculta />
    </div>
  );
}

export function MarqueeConferencias(): JSX.Element {
  const metade = Math.ceil(CONFERENCIAS.length / 2);
  return (
    <div
      className="flex flex-col gap-3.5"
      aria-label="As conferências que o sistema aplica"
    >
      {/*
        Durações DIFERENTES para velocidades IGUAIS. As duas fileiras têm o
        mesmo número de rótulos e larguras diferentes (5.600px e 6.422px,
        medidas), então uma duração só as faria correr a 78 e 89px/s — e duas
        velocidades lado a lado chamam atenção para a diferença, não para o
        conteúdo. Estes números dão ~75px/s nas duas, o ritmo de leitura.
      */}
      <Fileira itens={CONFERENCIAS.slice(0, metade)} duracao={96} />
      <Fileira itens={CONFERENCIAS.slice(metade)} duracao={110} reversa />
    </div>
  );
}
